import { LockOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import { useEntityActions } from 'api/entity';
import EditableTextField from 'components/editableTextField';
import { EntityDescriptionProps } from 'components/EntityDescription';
import { useEntityEncryption } from 'providers/EncryptionProvider';
import React, { useEffect, useState } from 'react';

import { buf2hex, decrypt, encrypt, keyTo256Hash } from './encryptUtils';

export function EncryptedDescription(props: EntityDescriptionProps) {
  const { entity } = props;

  const [open, setOpen] = useState(false);
  const [decryptKey, setDecryptKey] = useState('');
  const [decryptedText, setDecryptedText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { updateEntity } = useEntityActions();

  const { encryptionKey, addEntityEncryptionKey } = useEntityEncryption(
    entity.id,
  );

  useEffect(() => {
    if (!open) {
      setDecryptKey('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      if (encryptionKey) {
        const description = entity.description || '';
        const decrypted = await decrypt(description, encryptionKey);
        setDecryptedText(decrypted);
      } else {
        setDecryptedText(null);
      }
    })();
  }, [encryptionKey, entity.description, entity.extraFields?.hash]);

  const handleConfirm = async () => {
    if (true as any) {
      const keyHash = buf2hex(await keyTo256Hash(decryptKey));

      if (keyHash !== entity.extraFields?.hash) {
        setError('Invalid decryption key');
        return;
      }

      setError(null);
      setOpen(false);
      addEntityEncryptionKey(decryptKey, entity);
      return;
    }
  };

  if (!entity.description || decryptedText) {
    return (
      <EditableTextField
        rich
        key={entity.id}
        fullWidth
        minRows={5}
        variant="outlined"
        value={decryptedText ? decryptedText : (entity.description ?? '')}
        placeholder="No description added, type here to add one"
        onChange={async (v) => {
          let key: string | null = decryptKey;
          if (!key) key = prompt('Enter passkey');
          if (!key) return;
          const encryptedData = await encrypt(v, key);
          await updateEntity(entity.id, {
            description: encryptedData.data,
            extraFields: {
              hash: encryptedData.key,
            },
          });
        }}
      />
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={4}
    >
      <LockOutlined fontSize="large" color="action" />
      <Typography variant="h6" mt={2}>
        Encrypted Data
      </Typography>
      {decryptedText && (
        <Box mt={3} width="100%" px={2}>
          <Typography variant="body1" align="center" gutterBottom>
            Decrypted Content:
          </Typography>
          <Box
            p={2}
            border={1}
            borderColor="grey.300"
            borderRadius={1}
            bgcolor="background.paper"
          >
            <Typography>{decryptedText}</Typography>
          </Box>
        </Box>
      )}
      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => setOpen(true)}
      >
        {decryptedText ? 'Change Key' : 'Decrypt'}
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Card>
          <CardHeader title="Enter Decryption Key" />
          <CardContent>
            <TextField
              autoFocus
              margin="dense"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              label="Decryption Key"
              fullWidth
              variant="outlined"
              value={decryptKey}
              onChange={(e) => setDecryptKey(e.target.value)}
              error={!!error}
              helperText={error}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleConfirm();
                }
              }}
            />
          </CardContent>
          <CardActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm} color="primary">
              Decrypt
            </Button>
          </CardActions>
        </Card>
      </Modal>
    </Box>
  );
}
